import { FC, useState, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import {
  Text,
  Checkbox,
  ControlledForm,
  ControlledInput,
  Collapse,
  CollapseItem,
  Content,
  Icon,
  Alert
} from '@/client/components'
import { useAuthUser, useRouterPush } from '@/client/hooks'
import { inchesToMm, ROUTER_PATH } from '@/client/utils'
import { schemaResolver } from './asserts'
import { UploadPDF } from './Steps'
import * as S from './styles'
import { OnlinePreflightProps, FileCheckerFormSchema } from '../../types'

export const FileChecker: FC<OnlinePreflightProps> = ({
  file_checker,
  dimension,
  width,
  height,
  options,
  opaque,
  foil,
  sorts_title,
  sorts,
  asserts_group,
  upload_button,
  upload_text,
  drag_and_drop,
  how_does_it_work_title,
  how_does_it_work_content,
  triangle_visible,
  preflight_mistakes,
  professional_assistance,
  artwork_service_long_text,
  artwork_service_button,
  artwork_service_image,
  preflight_success,
  success_long_text,
  calculator_button,
  calculator_link,
  artwork_service_link,
  first_waiting_text,
  second_waiting_text,
  login_model
}) => {
  const { user, isAuthenticated } = useAuthUser()
  const { defaultLocale, routerPush } = useRouterPush()
  const cID = (user?.id ?? '') as string
  const [checkOpaque, setCheckOpaque] = useState(false)
  const [checkFoil, setCheckFoil] = useState(false)
  const formMethods = useForm<FileCheckerFormSchema>({
    resolver: schemaResolver(asserts_group),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      sorts: '',
      height: '',
      width: '',
      opaque: '',
      foil: ''
    }
  })
  const { isDirty, isValid } = formMethods.formState

  const handleCheckbox = useCallback(
    (name) => {
      if (name === 'opaque') {
        const newValue = checkOpaque ? '0' : '1'
        setCheckOpaque(!checkOpaque)
        formMethods.setValue('opaque', newValue, { shouldDirty: true })
      }

      if (name === 'foil') {
        const newValue = checkFoil ? '0' : '1'
        setCheckFoil(!checkFoil)
        formMethods.setValue('foil', newValue, { shouldDirty: true })
      }
    },
    [checkFoil, checkOpaque]
  )

  // The preflight API always expects millimetres, so convert on the US site
  // where the inputs are in inches. `|| 0` keeps the previous behaviour for
  // empty inputs (the form starts with '' before the user types).
  const widthInMm =
    defaultLocale === 'us' ? inchesToMm(formMethods.getValues('width')) || 0 : +formMethods.getValues('width') || 0

  const heightInMm =
    defaultLocale === 'us' ? inchesToMm(formMethods.getValues('height')) || 0 : +formMethods.getValues('height') || 0

  return (
    <>
      <S.FileChecker>
        <S.TopTriangle className='triangle' isVisible={!triangle_visible} />

        <S.CardHeader>
          <Text size='lg' family='heading' weight='bold' margin='0' color='inverse' padding='0.8rem  2.5rem'>
            <Icon name='file-checker' size='s5' />
            {file_checker}
          </Text>
        </S.CardHeader>

        <S.Form>
          <ControlledForm formMethods={formMethods}>
            <S.Block>
              <Text size='lg' family='heading' weight='bold' margin='0 0 1.5rem'>
                {dimension}
              </Text>

              <S.Option>
                {!+formMethods.getValues('width') && !+formMethods.getValues('height') && (
                  <div className='outer-circle'>
                    <div className='inner-circle'></div>
                  </div>
                )}

                <ControlledInput
                  name='width'
                  dimension='large'
                  type='number'
                  min={defaultLocale === 'us' ? 0.75 : 19}
                  max={defaultLocale === 'us' ? 24 : 585}
                  label={width}
                />

                <ControlledInput
                  name='height'
                  dimension='large'
                  type='number'
                  min={defaultLocale === 'us' ? 0.75 : 19}
                  max={defaultLocale === 'us' ? 11.5 : 308}
                  label={height}
                />
              </S.Option>
            </S.Block>

            <S.Block>
              <Text size='lg' family='heading' weight='bold' margin='0 0 1.5rem'>
                {sorts_title}
              </Text>
              <S.Option>
                <ControlledInput name='sorts' dimension='large' type='number' label={sorts} />
              </S.Option>
            </S.Block>

            <S.Block>
              <Text size='lg' family='heading' weight='bold' margin='0 0 1.5rem'>
                {options}
              </Text>
              <S.Option>
                <Checkbox
                  id='opaque'
                  value={checkOpaque ? '1' : '0'}
                  label={opaque}
                  checked={checkOpaque}
                  kind='square'
                  {...formMethods.register('opaque')}
                  onChange={() => handleCheckbox('opaque')}
                />
                <Checkbox
                  id='foil'
                  value={checkFoil ? '1' : '0'}
                  label={foil}
                  checked={checkFoil}
                  kind='square'
                  {...formMethods.register('foil')}
                  onChange={() => handleCheckbox('foil')}
                />
              </S.Option>
            </S.Block>

            <UploadPDF
              key='UploadStep'
              disabled={!isDirty || !isValid}
              upload_button={upload_button}
              upload_text={upload_text}
              drag_and_drop={drag_and_drop}
              cID={cID}
              width={widthInMm}
              height={heightInMm}
              opaque={+formMethods.getValues('opaque')}
              foil={+formMethods.getValues('foil')}
              preflight_mistakes={preflight_mistakes}
              professional_assistance={professional_assistance}
              artwork_service_long_text={artwork_service_long_text}
              artwork_service_button={artwork_service_button}
              artwork_service_image={artwork_service_image}
              preflight_success={preflight_success}
              success_long_text={success_long_text}
              calculator_button={calculator_button}
              calculator_link={calculator_link}
              artwork_service_link={artwork_service_link}
              first_waiting_text={first_waiting_text}
              second_waiting_text={second_waiting_text}
            />

            <S.LogInModal className={'modal-open'}>
              {!isAuthenticated && (
                <Alert
                  size='small'
                  confirmButtonName={login_model.log_in}
                  secondButtonName={login_model.register}
                  close={() => routerPush(ROUTER_PATH.LOGIN[defaultLocale])}
                  closeSecondButton={() => routerPush(ROUTER_PATH.SIGNUP[defaultLocale])}
                  overflow={false}
                  noCancelButton={true}
                >
                  <Icon name='proof-log-in' size='s8' />
                  <Text size='lg' family='heading' weight='bold' align='center' margin='1rem 0'>
                    {login_model.login_info}
                  </Text>
                </Alert>
              )}
            </S.LogInModal>
          </ControlledForm>
        </S.Form>

        <Collapse>
          <CollapseItem key={how_does_it_work_title} title={how_does_it_work_title}>
            <Content
              content={how_does_it_work_content}
              style={{ fontSize: '0.85rem', lineHeight: '1.4', color: 'black' }}
            />
          </CollapseItem>
        </Collapse>

        <S.DividerLine />
      </S.FileChecker>
    </>
  )
}
